<template>
  <div>
    <Head title="Editeaza cursul userului" />
    <h1 class="text-2xl font-bold mb-4">
      <Link
        class="text-indigo-500 hover:text-indigo-600"
        :href="$routes.user(userCourse.user.id)"
      >
        {{ userCourse.user.first_name + ' ' + userCourse.user.last_name }}
        / </Link
      >Editeaza cursul userului
    </h1>
    <user-course-form
      v-model="form"
      :available-courses="availableCourses"
      :hide-course-picker="true"
      :existing-diploma="existingDiploma"
      :existing-grades-paper="existingGradesPaper"
      class="max-w-3xl bg-white"
      @submit="submit"
      @update:existing-diploma="existingDiploma = $event"
      @update:existing-grades-paper="existingGradesPaper = $event"
    >
      <div
        class="flex items-center justify-end border-t border-gray-200 bg-gray-100 px-8 py-4"
      >
        <loading-button
          :loading="form.processing"
          class="btn-teal ml-auto"
          type="submit"
        >
          Salveaza
        </loading-button>
      </div>
    </user-course-form>

    <div
      v-if="userCourse.exam_results?.length"
      class="mt-6 max-w-3xl bg-white rounded-sm shadow-sm p-4"
    >
      <h2 class="text-xl font-bold mb-4">Examene</h2>
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left font-semibold border-b">
            <th class="pb-2 pr-4">Subiect</th>
            <th class="pb-2 pr-4">Data</th>
            <th class="pb-2 pr-4">Scor</th>
            <th class="pb-2">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(exam, i) in userCourse.exam_results"
            :key="i"
            class="border-t"
          >
            <td class="py-2 pr-4">{{ exam.subject_name }}</td>
            <td class="py-2 pr-4">{{ formatDate(exam.date) }}</td>
            <td class="py-2 pr-4">
              {{
                quizResultPercentage(exam.correct_count, exam.total_questions)
              }}%
              <span class="text-gray-500">
                ({{ exam.correct_count }}/{{ exam.total_questions }})
              </span>
            </td>
            <td class="py-2">
              <span
                :class="
                  exam.passed
                    ? 'text-green-600 font-semibold'
                    : 'text-red-600 font-semibold'
                "
              >
                {{ exam.passed ? 'Promovat' : 'Nepromovat' }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3';
import Layout from '@/Layouts/Main.vue';
import LoadingButton from '@/Shared/LoadingButton.vue';
import UserCourseForm from './Form.vue';
import {
  toDateString,
  toTimeString,
  formatDate,
  quizResultPercentage,
} from '@/utils/formatters';

export default {
  components: { Head, Link, LoadingButton, UserCourseForm },
  layout: Layout,
  props: {
    userCourse: {
      type: Object,
      required: true,
    },
    availableCourses: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      existingDiploma: this.userCourse.diploma_filename || null,
      existingGradesPaper: this.userCourse.grades_paper_filename || null,
      form: this.$inertia.form({
        user_course: {
          course: this.userCourse.course || null,
          reschedule_reason: this.userCourse.reschedule_reason || '',
          rescheduled: !!this.userCourse.rescheduled,
          finish_status: this.userCourse.finish_status || null,
          start_date: this.parseDateString(this.userCourse.start_date),
          end_date: this.parseDateString(this.userCourse.end_date),
          project_date: this.parseDateString(this.userCourse.project_date),
          presentation_date: this.parseDateString(
            this.userCourse.presentation_date,
          ),
          presentation_start_time: this.parseTimeString(
            this.userCourse.presentation_start_time,
          ),
          presentation_end_time: this.parseTimeString(
            this.userCourse.presentation_end_time,
          ),
          date_reschedule_requested: this.parseDateString(
            this.userCourse.date_reschedule_requested,
          ),
          restart_start_date: this.parseDateString(
            this.userCourse.restart_start_date,
          ),
          restart_end_date: this.parseDateString(
            this.userCourse.restart_end_date,
          ),
        },
      }),
    };
  },
  computed: {},
  methods: {
    toDateString,
    toTimeString,
    formatDate,
    quizResultPercentage,
    parseDateString(val) {
      if (!val) return null;
      if (val instanceof Date) return val;
      const m = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/.exec(val);
      if (m) {
        const y = parseInt(m[1]);
        const mo = parseInt(m[2]);
        const d = parseInt(m[3]);
        return new Date(y, mo - 1, d);
      }
      const dval = new Date(val);
      return isNaN(dval.getTime()) ? null : dval;
    },
    parseTimeString(val) {
      if (!val) return null;
      if (val instanceof Date) return val;
      const m = /^([0-9]{2}):([0-9]{2})$/.exec(val);
      if (m) {
        const h = parseInt(m[1]);
        const mi = parseInt(m[2]);
        const d = new Date();
        d.setHours(h, mi, 0, 0);
        return d;
      }
      const dval = new Date(val);
      return isNaN(dval.getTime()) ? null : dval;
    },
    submit() {
      const id = this.userCourse.id;
      this.form
        .transform((data) => {
          const formData = {
            ...data,
            user_course: {
              ...data.user_course,
              rescheduled: !!data.user_course.rescheduled,
              finish_status: data.user_course.finish_status || null,
              start_date: this.toDateString(data.user_course.start_date),
              end_date: this.toDateString(data.user_course.end_date),
              presentation_date: this.toDateString(
                data.user_course.presentation_date,
              ),
              presentation_start_time: this.toTimeString(
                data.user_course.presentation_start_time,
              ),
              presentation_end_time: this.toTimeString(
                data.user_course.presentation_end_time,
              ),
              project_date: this.toDateString(data.user_course.project_date),
              date_reschedule_requested: this.toDateString(
                data.user_course.date_reschedule_requested,
              ),
              restart_start_date: this.toDateString(
                data.user_course.restart_start_date,
              ),
              restart_end_date: this.toDateString(
                data.user_course.restart_end_date,
              ),
            },
          };

          // Add files if they exist
          if (data.user_course.diploma) {
            formData.diploma = data.user_course.diploma;
          }
          if (data.user_course.grades_paper) {
            formData.grades_paper = data.user_course.grades_paper;
          }
          if (data.user_course.remove_diploma) {
            formData.remove_diploma = true;
          }
          if (data.user_course.remove_grades_paper) {
            formData.remove_grades_paper = true;
          }

          return formData;
        })
        .put(this.$routes.user_course(id), {
          forceFormData: true,
        });
    },
  },
};
</script>
